import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function ProviderUsageReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [reportData, setReportData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showProviderChart, setShowProviderChart] = useState(false);
  const [showLocationChart, setShowLocationChart] = useState(false);
  const [chartData, setChartData] = useState({ providers: {}, locations: {} });

  // All location tables
  const locationTables = [
    'mayo_21',
    'mayo_22',
    'mayo_23',
    'mayo_24',
    'mayo_25',
    'ems_station'
  ];

  const loadUsers = async () => {
    try {
      const userSet = new Set();

      const promises = locationTables.map(async (table) => {
        const { data } = await supabase
          .from(table)
          .select('first_name, last_name')
          .not('first_name', 'is', null)
          .not('last_name', 'is', null);

        return data || [];
      });

      const results = await Promise.all(promises);

      results.flat().forEach(record => {
        const fullName = `${record.last_name || ''} ${record.first_name || ''}`.trim();
        if (fullName && fullName !== ' ') {
          userSet.add(fullName);
        }
      });

      setUsers(Array.from(userSet).sort());
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);
    setChartData({ providers: {}, locations: {} });

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const allData = [];
      const providerCounts = {};
      const locationCounts = {};

      // Query all location tables
      const promises = locationTables.map(async (table) => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .not('first_name', 'is', null)
          .not('last_name', 'is', null)
          .order('created_at', { ascending: false });

        if (error) {
          console.error(`Error querying ${table}:`, error);
          return [];
        }

        return (data || []).map(record => ({ ...record, table }));
      });

      const results = await Promise.all(promises);
      const flatResults = results.flat();

      // Process and filter data
      flatResults.forEach(record => {
        if (!record.created_at) return;

        // Parse date from created_at timestamp
        const recordDate = new Date(record.created_at);

        if (recordDate < start || recordDate > end) return;

        const fullName = `${record.last_name || ''} ${record.first_name || ''}`.trim();

        // Apply user filter if selected
        if (selectedUser && selectedUser !== fullName) return;

        // Get location from record or infer from table name
        const location = record.location || record.table.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        // Format date and time from created_at
        const dateFormatted = recordDate.toLocaleDateString('en-US');
        const timeFormatted = recordDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

        // Add to results
        allData.push({
          location: location,
          date: dateFormatted,
          time: timeFormatted,
          runNumber: record.run_number || '-',
          lastName: record.last_name || '',
          firstName: record.first_name || ''
        });

        // Count for charts
        if (fullName) {
          providerCounts[fullName] = (providerCounts[fullName] || 0) + 1;
        }
        if (location) {
          locationCounts[location] = (locationCounts[location] || 0) + 1;
        }
      });

      setReportData(allData);
      setChartData({ providers: providerCounts, locations: locationCounts });
    } catch (error) {
      console.error('Error running report:', error);
      alert('Error running report. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sortedData);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Location', 'Date', 'Time', 'Run #', 'Last Name', 'First Name'];
    const csvData = [
      headers.join(','),
      ...reportData.map(row =>
        [row.location, row.date, row.time, row.runNumber, row.lastName, row.firstName]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'patient_use_restock_report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderChart = (data, title) => {
    const entries = Object.entries(data);
    if (entries.length === 0) return null;

    const maxCount = Math.max(...entries.map(([, count]) => count));

    return (
      <div style={{ margin: '20px 0' }}>
        <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#333', fontSize: '16px', fontWeight: '600' }}>
          {title}
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {entries.map(([name, count]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                minWidth: '150px',
                fontSize: '13px',
                textAlign: 'right',
                color: '#333'
              }}>
                {name}:
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
                height: '28px',
                width: `${(count / maxCount) * 400}px`,
                minWidth: '30px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(14,50,147,0.3)'
              }}>
                {count}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Load all users for the dropdown on mount
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Access control - after all hooks
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: '#d32f2f',
          color: 'white',
          padding: '30px',
          borderRadius: '10px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '10px', color: 'white' }}>
            Patient Use/Restock Report
          </h1>
          <p style={{ fontSize: '14px', margin: 0, color: 'white', lineHeight: '1.6' }}>
            Comprehensive view of narcotic usage and restocking events by provider, detailing location, date, time and personnel.
          </p>
        </div>

        {/* Filters */}
        <div className="user-action-bar report-filters" style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          gap: '15px',
          flexWrap: 'wrap',
          alignItems: 'end'
        }}>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Filter by User
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '0 1 auto' }}>
            <button
              onClick={runReport}
              disabled={loading}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: loading ? '#ccc' : '#0E3293',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Running...' : 'Run Report'}
            </button>
          </div>

          <div style={{ flex: '0 1 auto' }}>
            <button
              onClick={exportToCSV}
              disabled={reportData.length === 0}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                color: '#333',
                fontSize: '13px',
                fontWeight: '600',
                cursor: reportData.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Export to CSV
            </button>
          </div>
        </div>

        {/* Chart Controls */}
        {reportData.length > 0 && (
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#333' }}>
              Data Visualization
            </h3>
            <div className="chart-buttons" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <button
                onClick={() => setShowProviderChart(!showProviderChart)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #0E3293',
                  borderRadius: '6px',
                  background: showProviderChart ? '#0E3293' : 'white',
                  color: showProviderChart ? 'white' : '#0E3293',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {showProviderChart ? 'Hide' : 'Show'} Provider Usage Chart
              </button>
              <button
                onClick={() => setShowLocationChart(!showLocationChart)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #0E3293',
                  borderRadius: '6px',
                  background: showLocationChart ? '#0E3293' : 'white',
                  color: showLocationChart ? 'white' : '#0E3293',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {showLocationChart ? 'Hide' : 'Show'} Location Usage Chart
              </button>
            </div>

            {showProviderChart && renderChart(chartData.providers, 'Provider Usage')}
            {showLocationChart && renderChart(chartData.locations, 'Location Usage')}
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '10px',
            textAlign: 'center',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <p style={{ color: '#0E3293', fontSize: '16px', fontWeight: '500' }}>
              Report is running... this may take a moment.
            </p>
          </div>
        )}

        {/* Results Table */}
        {reportData.length > 0 && (
          <div className="report-table-container" style={{
            background: 'white',
            borderRadius: '10px',
            overflow: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ padding: '20px 20px 10px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', margin: 0 }}>
                Report Results ({reportData.length} records found)
              </h3>
            </div>
            <div className="table-scroll" style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {[
                      { key: 'location', label: 'Location' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'runNumber', label: 'Run #' },
                      { key: 'lastName', label: 'Last Name' },
                      { key: 'firstName', label: 'First Name' }
                    ].map(column => (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '2px solid #0E3293',
                          fontWeight: '600',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.label} {getSortIcon(column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '12px', color: '#333' }}>{row.location}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.date}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.time}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.runNumber}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.lastName}</td>
                      <td style={{ padding: '12px', color: '#333' }}>{row.firstName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && reportData.length === 0 && startDate && endDate && (
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '10px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <p style={{ color: '#666', fontSize: '16px' }}>
              No patient use/restock records found for the selected criteria.
            </p>
          </div>
        )}
      </div>

      <footer style={{
        textAlign: 'center',
        padding: '20px',
        color: '#666',
        fontSize: '12px'
      }}>
        <p>Provider Usage Report - NarcoticTrack © 2025</p>
      </footer>
    </div>
  );
}

export default ProviderUsageReport;
